// ---------------------------------------------------------------------------
// pleasantPlus — the drive meter + subscription gate (Morgan, July 28:
// "$1.99 a month … after 4 drives").
//
// The deal, in her words: the first four drives are on us. Starting the
// fifth navigation asks for Pleasant Monthly ($1.99/month, auto-renewable,
// product `pleasant.monthly.199`) through RevenueCat, which owns receipts,
// restores, and Apple's billing plumbing.
//
// Design rules:
//   • The METER counts navigation STARTS (ending a drive early still used
//     a drive — no gaming), local + mirrored to the Supabase profile when
//     signed in, so a reinstall doesn't reset it.
//   • FAIL OPEN. If RevenueCat isn't configured (no key in .env), or the
//     network is down, or anything throws — the app behaves as if
//     subscribed. A billing hiccup must NEVER strand a driver.
//   • CarPlay can't sell (Apple rule): the car speaks a gentle line and
//     the phone shows the paywall.
// ---------------------------------------------------------------------------
import { Capacitor } from "@capacitor/core";

const FREE_DRIVES = 4;
const COUNT_KEY = "pleasant.driveCount.v1";
const GRANDFATHER_KEY = "pleasant.grandfathered.v1";
/** Accounts created before pricing shipped stay free forever (Morgan:
 *  "people who have already downloaded are grandfathered in"). Set to the
 *  2.2 release day before submitting. */
const PRICING_CUTOFF_ISO = "2026-08-04T00:00:00Z";

/** Early believers ride free forever. Two signals, evaluated once and
 *  remembered: (a) any pre-pricing local traces (trip history, saved
 *  places) at first launch of this version; (b) a Supabase account created
 *  before the cutoff — which also survives reinstalls. */
export function isGrandfathered(): boolean {
  try {
    if (localStorage.getItem(GRANDFATHER_KEY) === "1") return true;
    const traces = ["pleasant.tripHistory.v1", "pleasant.savedPlaces.v1", "pleasant.routesEtaNotice.v1"];
    if (traces.some((k) => !!localStorage.getItem(k))) {
      localStorage.setItem(GRANDFATHER_KEY, "1");
      return true;
    }
  } catch { /* fall through */ }
  return false;
}

/** Reinstall case: account age proves early-believer status. */
export async function syncGrandfatherFromAccount(): Promise<void> {
  try {
    if (localStorage.getItem(GRANDFATHER_KEY) === "1") return;
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.auth.getUser();
    const created = data.user?.created_at;
    if (created && new Date(created) < new Date(PRICING_CUTOFF_ISO)) {
      localStorage.setItem(GRANDFATHER_KEY, "1");
    }
  } catch { /* best effort */ }
}

const RC_KEY = (import.meta.env.VITE_REVENUECAT_APPLE_KEY as string | undefined) ?? "";
export const ENTITLEMENT_ID = "plus";

let configured = false;

/** Configure RevenueCat once at boot (no-op on web / without a key). */
export async function configurePurchases(appUserId?: string | null): Promise<void> {
  if (configured || !RC_KEY || !Capacitor.isNativePlatform()) return;
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    await Purchases.configure({ apiKey: RC_KEY, appUserID: appUserId ?? undefined });
    configured = true;
  } catch {
    /* fail open */
  }
}

/** Is the subscription active? Fails OPEN (true) when billing is
 *  unreachable or unconfigured — never strand a driver on an edge case. */
export async function hasPlus(): Promise<boolean> {
  if (!RC_KEY || !Capacitor.isNativePlatform()) return true; // not selling yet
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const info = await Purchases.getCustomerInfo();
    return ENTITLEMENT_ID in (info.customerInfo.entitlements.active ?? {});
  } catch {
    return true; // fail open
  }
}

export function driveCount(): number {
  try {
    return Number(localStorage.getItem(COUNT_KEY) ?? "0") || 0;
  } catch {
    return 0;
  }
}

/** Record a navigation start. Mirrors to the Supabase profile (best
 *  effort) so reinstalling doesn't hand out four more drives. */
export function recordDriveStart(): void {
  try {
    const n = driveCount() + 1;
    localStorage.setItem(COUNT_KEY, String(n));
    void (async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data } = await supabase.auth.getUser();
        if (data.user) {
          await supabase.from("profiles").update({ drive_count: n }).eq("user_id", data.user.id);
        }
      } catch { /* best effort */ }
    })();
  } catch { /* best effort */ }
}

/** On sign-in, adopt the server's count when it's higher (reinstall case). */
export async function syncDriveCountFromProfile(): Promise<void> {
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.auth.getUser();
    if (!data.user) return;
    const { data: prof } = await supabase
      .from("profiles").select("drive_count").eq("user_id", data.user.id).maybeSingle();
    const server = Number(prof?.drive_count ?? 0) || 0;
    if (server > driveCount()) localStorage.setItem(COUNT_KEY, String(server));
  } catch { /* best effort */ }
}

/** The gate. True → let the drive start. False → show the paywall. */
export async function mayStartDrive(): Promise<boolean> {
  if (isGrandfathered()) return true;
  if (driveCount() < FREE_DRIVES) return true;
  return hasPlus();
}

export function freeDrivesLeft(): number {
  return Math.max(0, FREE_DRIVES - driveCount());
}

/** Kick off the purchase. Returns true when the entitlement is active. */
export async function purchasePlus(): Promise<boolean> {
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const offerings = await Purchases.getOfferings();
    const pkg = offerings.current?.availablePackages?.[0];
    if (!pkg) return false;
    const res = await Purchases.purchasePackage({ aPackage: pkg });
    return ENTITLEMENT_ID in (res.customerInfo.entitlements.active ?? {});
  } catch {
    return false;
  }
}

/** Restore purchases (required by App Review). */
export async function restorePlus(): Promise<boolean> {
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const res = await Purchases.restorePurchases();
    return ENTITLEMENT_ID in (res.customerInfo.entitlements.active ?? {});
  } catch {
    return false;
  }
}
